"use client";

import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "peptide-recently-viewed";
const MAX_ITEMS = 10;

export interface RecentlyViewedItem {
	slug: string;
	name: string;
	price: number | null;
	currency: string;
	image: string | null;
	reviewAvg?: number;
	reviewCount?: number;
	locale?: string;
	channel?: string;
}

export function scopeRecentlyViewedItems(items: RecentlyViewedItem[], locale: string, channel: string) {
	return items.filter((item) => item.locale === locale && item.channel === channel);
}

export function recentlyViewedStorageKey(locale: string, channel: string) {
	return `${STORAGE_KEY}:${locale}:${channel}`;
}

function readRecentlyViewedItems(locale: string, channel: string): RecentlyViewedItem[] {
	try {
		const raw = localStorage.getItem(recentlyViewedStorageKey(locale, channel));
		const parsed: unknown = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed)
			? scopeRecentlyViewedItems(parsed as RecentlyViewedItem[], locale, channel)
			: [];
	} catch {
		return [];
	}
}

export function useRecentlyViewed(locale: string, channel: string) {
	const [items, setItems] = useState<RecentlyViewedItem[]>([]);
	const [loadedScope, setLoadedScope] = useState<string | null>(null);
	const scope = recentlyViewedStorageKey(locale, channel);
	const scopeRef = useRef({ locale, channel, scope });
	const loadedScopeRef = useRef<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		scopeRef.current = { locale, channel, scope };
		queueMicrotask(() => {
			if (cancelled) return;
			if (scopeRef.current.scope !== scope) return;
			setItems(readRecentlyViewedItems(locale, channel));
			loadedScopeRef.current = scope;
			setLoadedScope(scope);
		});
		return () => {
			cancelled = true;
		};
	}, [channel, locale, scope]);

	const add = useCallback((item: RecentlyViewedItem) => {
		const currentScope = scopeRef.current;
		const itemScope = {
			locale: item.locale ?? currentScope.locale,
			channel: item.channel ?? currentScope.channel,
		};
		const itemStorageKey = recentlyViewedStorageKey(itemScope.locale, itemScope.channel);
		const scopedItem = { ...item, ...itemScope };
		const appendItem = (existing: RecentlyViewedItem[]) => {
			const filtered = existing.filter((p) => p.slug !== scopedItem.slug);
			return [scopedItem, ...filtered].slice(0, MAX_ITEMS);
		};

		// A retained event callback can run after navigation has started but before
		// React commits the new props. Its item still identifies the destination;
		// write there without replacing the cards from the currently rendered scope.
		if (itemStorageKey !== currentScope.scope) {
			const next = appendItem(readRecentlyViewedItems(itemScope.locale, itemScope.channel));
			try {
				localStorage.setItem(itemStorageKey, JSON.stringify(next));
			} catch {
				// Ignore quota / private mode
			}
			return;
		}

		setItems((prev) => {
			const currentItems =
				loadedScopeRef.current === currentScope.scope
					? prev
					: readRecentlyViewedItems(currentScope.locale, currentScope.channel);
			const next = appendItem(currentItems);
			try {
				localStorage.setItem(currentScope.scope, JSON.stringify(next));
			} catch {
				// Ignore quota / private mode
			}
			return next;
		});
		loadedScopeRef.current = currentScope.scope;
		setLoadedScope(currentScope.scope);
	}, []);

	const mounted = loadedScope === scope;
	return { items: mounted ? items : [], add, mounted };
}
